import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Fade, Zoom, useTheme } from '@mui/material';
import {
  LightbulbOutlined,
  Description,
  Code,
  RocketLaunch,
  CheckCircle,
  AutoAwesome,
} from '@mui/icons-material';

interface AnimationStep {
  id: number;
  phase: 'idea' | 'spec' | 'code' | 'deploy' | 'complete';
  delay: number;
}

const SpecificationFlowAnimation: React.FC = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [showCards, setShowCards] = useState<boolean[]>([false, false, false]);

  const steps: AnimationStep[] = [
    { id: 0, phase: 'idea', delay: 0 },
    { id: 1, phase: 'spec', delay: 4000 },
    { id: 2, phase: 'code', delay: 8000 },
    { id: 3, phase: 'deploy', delay: 12000 },
    { id: 4, phase: 'complete', delay: 16000 },
  ];

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    const runAnimation = () => {
      steps.forEach((step, index) => {
        const timer = setTimeout(() => {
          setActiveStep(index);
          
          // Show specification cards progressively
          if (index === 1) {
            setTimeout(() => setShowCards([true, false, false]), 300);
            setTimeout(() => setShowCards([true, true, false]), 600);
            setTimeout(() => setShowCards([true, true, true]), 900);
          }
        }, step.delay);
        timers.push(timer);
      });

      // Loop the animation - restart after showing complete step
      const loopTimer = setTimeout(() => {
        setActiveStep(0);
        setShowCards([false, false, false]);
        runAnimation(); // Recursive call to loop
      }, 20000);
      timers.push(loopTimer);
    };

    runAnimation();

    return () => timers.forEach(timer => clearTimeout(timer));
  }, []); // Empty dependency array - only run once on mount

  const getStepContent = () => {
    switch (steps[activeStep]?.phase) {
      case 'idea':
        return (
          <Zoom in timeout={500}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                minHeight: 80,
              }}
            >
              <LightbulbOutlined sx={{ fontSize: 40, color: '#FFC107' }} />
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Product Idea
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  Build a customer portal
                </Typography>
              </Box>
            </Paper>
          </Zoom>
        );

      case 'spec':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Fade in timeout={500}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Description sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                <Typography variant="h6" fontWeight={600} sx={{ color: 'white' }}>
                  Specifications
                </Typography>
                <AutoAwesome sx={{ fontSize: 20, color: '#FFD700', ml: 1 }} />
              </Box>
            </Fade>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {showCards[0] && (
                <Zoom in timeout={400}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(255,255,255,0.95)',
                      flex: '1 1 180px',
                      minWidth: 150,
                      border: `2px solid ${theme.palette.primary.light}`,
                    }}
                  >
                    <Typography variant="caption" color="primary" fontWeight={600}>
                      USER STORY #1
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                      User Authentication
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Login, Register, MFA
                    </Typography>
                  </Paper>
                </Zoom>
              )}

              {showCards[1] && (
                <Zoom in timeout={400}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(255,255,255,0.95)',
                      flex: '1 1 180px',
                      minWidth: 150,
                      border: `2px solid ${theme.palette.secondary.light}`,
                    }}
                  >
                    <Typography variant="caption" color="secondary" fontWeight={600}>
                      USER STORY #2
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                      Dashboard
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Analytics, Widgets
                    </Typography>
                  </Paper>
                </Zoom>
              )}

              {showCards[2] && (
                <Zoom in timeout={400}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(255,255,255,0.95)',
                      flex: '1 1 180px',
                      minWidth: 150,
                      border: `2px solid ${theme.palette.success.light}`,
                    }}
                  >
                    <Typography variant="caption" color="success.main" fontWeight={600}>
                      USER STORY #3
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                      Profile Settings
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Edit, Preferences
                    </Typography>
                  </Paper>
                </Zoom>
              )}
            </Box>
          </Box>
        );

      case 'code':
        return (
          <Fade in timeout={500}>
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                fontFamily: 'monospace',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Code sx={{ fontSize: 24, color: '#4EC9B0' }} />
                <Typography variant="body2" fontWeight={600} sx={{ color: '#4EC9B0' }}>
                  Generated Code
                </Typography>
              </Box>
              
              <Box component="pre" sx={{ m: 0, fontSize: '0.75rem', lineHeight: 1.6, fontFamily: 'inherit' }}>
                <Box component="code">
                  <span style={{ color: '#C586C0' }}>const</span>{' '}
                  <span style={{ color: '#4FC1FF' }}>UserAuth</span> ={' '}
                  <span style={{ color: '#DCDCAA' }}>{'() => {'}</span>
                  {'\n  '}
                  <span style={{ color: '#C586C0' }}>return</span> (
                  {'\n    '}
                  <span style={{ color: '#4EC9B0' }}>{'<AuthProvider>'}</span>
                  {'\n      '}
                  <span style={{ color: '#4EC9B0' }}>{'<LoginForm'}</span>{' '}
                  <span style={{ color: '#9CDCFE' }}>mfa</span>
                  <span style={{ color: '#CE9178' }}>=&#123;true&#125;</span>{' '}
                  <span style={{ color: '#4EC9B0' }}>{'/>'}</span>
                  {'\n    '}
                  <span style={{ color: '#4EC9B0' }}>{'</AuthProvider>'}</span>
                  {'\n  '});
                  {'\n'}<span style={{ color: '#DCDCAA' }}>{'};'}</span>
                </Box>
              </Box>

              {/* Animated cursor */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  width: 2,
                  height: 16,
                  bgcolor: '#4EC9B0',
                  animation: 'blink 1s infinite',
                  '@keyframes blink': {
                    '0%, 49%': { opacity: 1 },
                    '50%, 100%': { opacity: 0 },
                  },
                }}
              />
            </Paper>
          </Fade>
        );

      case 'deploy':
        return (
          <Zoom in timeout={500}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                minHeight: 100,
              }}
            >
              <RocketLaunch sx={{ fontSize: 48, color: theme.palette.primary.main }} />
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Deploying...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Building, Testing, Launching
                </Typography>
              </Box>
            </Paper>
          </Zoom>
        );

      case 'complete':
        return (
          <Zoom in timeout={500}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 2,
                bgcolor: theme.palette.success.main,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                minHeight: 100,
              }}
            >
              <CheckCircle sx={{ fontSize: 56 }} />
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Live & Ready!
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Your customer portal is deployed
                </Typography>
              </Box>
            </Paper>
          </Zoom>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 2, md: 4 },
        minHeight: { xs: 300, md: 400 },
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 60,
          height: 60,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.1)',
          animation: 'float 3s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          right: '15%',
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.1)',
          animation: 'float 4s ease-in-out infinite 1s',
        }}
      />

      {/* Main animation content */}
      <Box sx={{ width: '100%', maxWidth: 500 }}>
        {getStepContent()}
      </Box>

      {/* Progress indicators */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mt: 4,
          justifyContent: 'center',
        }}
      >
        {steps.map((step, index) => (
          <Box
            key={step.id}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: activeStep === index ? 'white' : 'rgba(255,255,255,0.3)',
              transition: 'all 0.3s ease',
              transform: activeStep === index ? 'scale(1.3)' : 'scale(1)',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default SpecificationFlowAnimation;
